import React, { useState, useEffect } from 'react';
import { API } from '../App';
import './AdminReferrals.css';

export default function AdminReferrals() {
  const [referralData, setReferralData] = useState(null);
  const [guestEmails, setGuestEmails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('referrals');
  const language = localStorage.getItem('language') || 'en';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const [referralsRes, emailsRes] = await Promise.all([
        fetch(`${API}/admin/referrals`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API}/admin/guest-emails`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      if (!referralsRes.ok) {
        throw new Error('Failed to fetch referrals');
      }

      const referralsData = await referralsRes.json();
      setReferralData(referralsData);

      if (emailsRes.ok) {
        const emailsData = await emailsRes.json();
        setGuestEmails(emailsData);
      }

      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-referrals">
        <h2>🔗 {language === 'de' ? 'Referrals & E-Mails' : 'Referrals & Emails'}</h2>
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-referrals">
        <h2>🔗 {language === 'de' ? 'Referrals & E-Mails' : 'Referrals & Emails'}</h2>
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="admin-referrals">
      <h2>🔗 {language === 'de' ? 'Referrals & E-Mails' : 'Referrals & Emails'}</h2>
      
      <div className="tabs">
        <button 
          className={`tab-btn ${activeTab === 'referrals' ? 'active' : ''}`}
          onClick={() => setActiveTab('referrals')}
        >
          👥 Referrals
        </button>
        <button 
          className={`tab-btn ${activeTab === 'emails' ? 'active' : ''}`}
          onClick={() => setActiveTab('emails')}
        >
          📧 {language === 'de' ? 'Gast-E-Mails' : 'Guest Emails'}
        </button>
      </div>

      {activeTab === 'referrals' && referralData && (
        <div className="referrals-section">
          {/* Summary Cards */}
          <div className="summary-cards">
            <div className="summary-card">
              <div className="card-value">{referralData.total_referrals || 0}</div>
              <div className="card-label">{language === 'de' ? 'Gesamt Referrals' : 'Total Referrals'}</div>
            </div>
            <div className="summary-card">
              <div className="card-value">{referralData.total_signups || 0}</div>
              <div className="card-label">{language === 'de' ? 'Anmeldungen' : 'Signups'}</div>
            </div>
            <div className="summary-card highlight">
              <div className="card-value">{referralData.total_conversions || 0}</div>
              <div className="card-label">{language === 'de' ? 'Konversionen' : 'Conversions'}</div>
            </div>
          </div>

          {/* Referrer Stats Table */}
          <h3>{language === 'de' ? 'Referrer Statistiken' : 'Referrer Statistics'}</h3>
          {referralData.referrer_stats && referralData.referrer_stats.length > 0 ? (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Typ</th>
                    <th>{language === 'de' ? 'Anmeldungen' : 'Signups'}</th>
                    <th>{language === 'de' ? 'Konversionen' : 'Conversions'}</th>
                    <th>{language === 'de' ? 'Umsatz' : 'Revenue'}</th>
                  </tr>
                </thead>
                <tbody>
                  {referralData.referrer_stats.map((stat, index) => (
                    <tr key={index}>
                      <td><code>{stat.code}</code></td>
                      <td>{stat.referrer_name || '-'}</td>
                      <td>
                        <span className={`type-badge ${stat.type}`}>
                          {stat.type === 'user' ? '👤 User' : '🤝 Affiliate'}
                        </span>
                      </td>
                      <td>{stat.total_signups}</td>
                      <td>{stat.total_conversions}</td>
                      <td>€{(stat.total_revenue || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="no-data">{language === 'de' ? 'Noch keine Referrals' : 'No referrals yet'}</p>
          )}

          {/* Recent Referrals */}
          <h3>{language === 'de' ? 'Letzte Referrals' : 'Recent Referrals'}</h3>
          {referralData.referrals && referralData.referrals.length > 0 ? (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{language === 'de' ? 'Datum' : 'Date'}</th>
                    <th>Code</th>
                    <th>{language === 'de' ? 'Angemeldet' : 'Signed Up'}</th>
                    <th>{language === 'de' ? 'Konvertiert' : 'Converted'}</th>
                  </tr>
                </thead>
                <tbody>
                  {referralData.referrals.slice(0, 20).map((ref, index) => (
                    <tr key={index}>
                      <td>{new Date(ref.created_at).toLocaleDateString()}</td>
                      <td><code>{ref.referrer_code}</code></td>
                      <td>{ref.signup_completed ? '✅' : '⏳'}</td>
                      <td>{ref.has_converted ? '💰' : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="no-data">{language === 'de' ? 'Noch keine Referrals' : 'No referrals yet'}</p>
          )}
        </div>
      )}

      {activeTab === 'emails' && (
        <div className="emails-section">
          {/* Summary Cards */}
          <div className="summary-cards">
            <div className="summary-card">
              <div className="card-value">{guestEmails?.total_count || 0}</div>
              <div className="card-label">{language === 'de' ? 'Gesamt E-Mails' : 'Total Emails'}</div>
            </div>
            <div className="summary-card highlight">
              <div className="card-value">{guestEmails?.opt_in_count || 0}</div>
              <div className="card-label">{language === 'de' ? 'Marketing Opt-In' : 'Marketing Opt-In'}</div>
            </div>
            <div className="summary-card">
              <div className="card-value">{guestEmails?.opt_out_count || 0}</div>
              <div className="card-label">{language === 'de' ? 'Nur Geschichte' : 'Story Only'}</div>
            </div>
          </div>

          {/* Guest Emails Table */}
          <h3>{language === 'de' ? 'Gesammelte E-Mails' : 'Collected Emails'}</h3>
          {guestEmails?.guest_emails && guestEmails.guest_emails.length > 0 ? (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{language === 'de' ? 'Datum' : 'Date'}</th>
                    <th>E-Mail</th>
                    <th>{language === 'de' ? 'Kind' : 'Child'}</th>
                    <th>{language === 'de' ? 'Marketing' : 'Marketing'}</th>
                  </tr>
                </thead>
                <tbody>
                  {guestEmails.guest_emails.map((email, index) => (
                    <tr key={index}>
                      <td>{new Date(email.sent_at).toLocaleDateString()}</td>
                      <td>
                        <a href={`mailto:${email.email}`} className="email-link">
                          {email.email}
                        </a>
                      </td>
                      <td>{email.child_name || '-'}</td>
                      <td>
                        {email.opt_in_marketing 
                          ? <span className="opt-in">✅ Opt-In</span>
                          : <span className="opt-out">❌ Opt-Out</span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="no-data">{language === 'de' ? 'Noch keine E-Mails gesammelt' : 'No emails collected yet'}</p>
          )}

          {/* Export Button */}
          {guestEmails?.guest_emails && guestEmails.guest_emails.length > 0 && (
            <button 
              className="export-btn"
              onClick={() => {
                const optInEmails = guestEmails.guest_emails
                  .filter(e => e.opt_in_marketing)
                  .map(e => e.email)
                  .join('\n');
                navigator.clipboard.writeText(optInEmails);
                alert(language === 'de' 
                  ? `${guestEmails.opt_in_count} Opt-In E-Mails kopiert!` 
                  : `${guestEmails.opt_in_count} opt-in emails copied!`);
              }}
            >
              📋 {language === 'de' ? 'Opt-In E-Mails kopieren' : 'Copy Opt-In Emails'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
